/**
 * FormField Component
 *
 * Reusable form field that wraps label, input, error message, and helper text.
 * Designed to work with react-hook-form's register function.
 */

import { InputHTMLAttributes, forwardRef } from 'react';
import { FieldError } from 'react-hook-form';

interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /** Field label text */
  label: string;
  /** Field name (used for id and register) */
  name: string;
  /** Error object from react-hook-form */
  error?: FieldError;
  /** Optional helper text below the input */
  helperText?: string;
  /** Whether the field is required (adds * to label) */
  required?: boolean;
}

/**
 * FormField renders a labeled input with error handling.
 *
 * Usage with react-hook-form:
 * ```tsx
 * <FormField
 *   label="Email"
 *   {...register('email')}
 *   error={errors.email}
 *   required
 * />
 * ```
 */
const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, name, error, helperText, required, type = 'text', ...inputProps }, ref) => {
    const inputClasses = `
      block w-full px-3 py-2 border rounded-md shadow-sm
      focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack
      ${error ? 'border-red-300' : 'border-gray-300'}
      ${inputProps.disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}
    `.trim();

    return (
      <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}{required && ' *'}
        </label>
        <input
          ref={ref}
          id={name}
          name={name}
          type={type}
          className={inputClasses}
          {...inputProps}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600">{error.message}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-xs text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
